import { firebaseAuth } from '../firebase/auth';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';

const API_URL = import.meta.env.VITE_API_URL;

export interface DeviceToken {
    _id: string;
    userId: string;
    token: string;
    device: {
        type: string;
        platform: string;
        model?: string;
        appVersion?: string;
    };
    isActive: boolean;
    lastUsed: Date;
    createdAt: Date;
    updatedAt: Date;
}

export interface PushToken {
    _id: string;
    userId: string;
    token: string;
    platform: string;
    deviceInfo?: Record<string, any>;
    preferences: {
        marketing: boolean;
        updates: boolean;
        activity: boolean;
    };
    createdAt: Date;
    updatedAt: Date;
}

// Interface for web push subscription
export interface WebPushSubscription {
    _id: string;
    userId: string;
    subscription: string; // JSON stringified PushSubscription
    browser: string;
    deviceInfo?: Record<string, any>;
    isActive: boolean;
    lastUsed: Date;
    createdAt: Date;
    updatedAt: Date;
}

export interface NotificationHistory {
    _id: string;
    title: string;
    body: string;
    data?: any;
    type: 'individual' | 'group' | 'topic' | 'all';
    status: 'pending' | 'sent' | 'failed' | 'partial';
    recipients: {
        userIds: string[];
        topic?: string;
        totalDevices: number;
    };
    analytics?: {
        openCount: number;
        openedBy: Array<{
            userId: string;
            deviceId?: string;
            timestamp: Date;
        }>;
    };
    sentAt?: Date;
    createdAt: Date;
}

export interface NotificationPreferences {
    marketing: boolean;
    updates: boolean;
    activity: boolean;
}

export interface UnreadCountResponse {
    unreadCount: number;
}

export interface NotificationsHistoryResponse {
    notifications: NotificationHistory[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

class NotificationsAPI {
    private deviceInfo: any = null;
    private currentToken: string | null = null;
    private webPushSubscription: PushSubscription | null = null;

    constructor() {
        // Inicializar información del dispositivo
        this.initDeviceInfo();
    }

    private async initDeviceInfo() {
        try {
            this.deviceInfo = await Device.getInfo();
        } catch (error) {
            console.error('Error al obtener información del dispositivo:', error);
        }
    }

    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const idToken = await firebaseAuth.getIdToken();
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
            ...options.headers
        };

        try {
            const response = await fetch(`${API_URL}${endpoint}`, {
                ...options,
                headers
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || 'Request failed');
            }

            return response.json();
        } catch (error) {
            console.error(`API Error (${endpoint}):`, error);
            throw error;
        }
    }

    /**
     * Establece el token actual para usar en operaciones
     */
    setCurrentToken(token: string) {
        this.currentToken = token;
    }

    /**
     * Obtiene el token actual
     */
    getCurrentToken(): string | null {
        return this.currentToken;
    }

    /**
     * Establece la suscripción web push actual
     */
    setWebPushSubscription(subscription: PushSubscription) {
        this.webPushSubscription = subscription;
    }

    /**
     * Obtiene la suscripción web push actual
     */
    getWebPushSubscription(): PushSubscription | null {
        return this.webPushSubscription;
    }

    /**
     * Detecta el tipo de navegador para mejorar la experiencia de notificaciones
     */
    private detectBrowser(): string {
        if (typeof window === 'undefined' || !window.navigator) {
            return 'unknown';
        }

        const userAgent = window.navigator.userAgent;

        // Detectar navegadores comunes
        if (userAgent.indexOf('Firefox') !== -1) {
            return 'firefox';
        } else if (userAgent.indexOf('Chrome') !== -1) {
            return 'chrome';
        } else if (userAgent.indexOf('Safari') !== -1) {
            return 'safari';
        } else if (userAgent.indexOf('Edge') !== -1 || userAgent.indexOf('Edg') !== -1) {
            return 'edge';
        } else if (userAgent.indexOf('Opera') !== -1 || userAgent.indexOf('OPR') !== -1) {
            return 'opera';
        }

        return 'other';
    }

    /**
     * Registra la suscripción de web push en el backend
     */
    async registerWebPushSubscription(
        subscription: PushSubscription,
        browser: string = 'other',
        deviceInfo: any = {},
        fcmToken: string | null = null
    ): Promise<any> {
        try {
            const userId = await this.getCurrentUserId();

            // Preparar datos de la suscripción
            const subscriptionData = {
                subscription: JSON.stringify(subscription),
                userId,
                browser,
                deviceInfo,
                fcmToken,
            };

            // Enviar al endpoint de suscripción web push
            const response = await this.request('/api/web-push/subscribe', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(subscriptionData)
            });

            return response;
        } catch (error) {
            console.error('Error registering web push subscription:', error);
            throw error;
        }
    }

    /**
     * Cancela la suscripción web push en el backend y el navegador
     */
    async unregisterWebPushSubscription(subscription: PushSubscription): Promise<any> {
        try {
            // Enviar al endpoint para cancelar suscripción
            const response = await this.request('/api/web-push/unsubscribe', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    subscription: JSON.stringify(subscription)
                })
            });

            return response;
        } catch (error) {
            console.error('Error unregistering web push subscription:', error);
            throw error;
        }
    }

    /**
    * Obtiene la clave pública VAPID del servidor
    */
    async getVapidPublicKey(): Promise<string> {
        try {
            const response = await this.request('/api/web-push/vapid-public-key');
            return response.publicKey;
        } catch (error) {
            console.error('Error getting VAPID public key:', error);
            throw error;
        }
    }

    /**
     * Comprueba si las notificaciones web push están permitidas
     */
    async checkWebPushPermission(): Promise<NotificationPermission> {
        if (typeof Notification === 'undefined') {
            throw new Error('Las notificaciones no están soportadas en este navegador');
        }

        return Notification.permission;
    }

    /**
     * Solicita permiso para notificaciones web push
     */
    async requestWebPushPermission(): Promise<NotificationPermission> {
        if (typeof Notification === 'undefined') {
            throw new Error('Las notificaciones no están soportadas en este navegador');
        }

        const permission = await Notification.requestPermission();
        return permission;
    }

    /**
     * Envía una notificación web push de prueba (solo en desarrollo)
     */
    async sendWebPushTest(
        title: string,
        body: string,
        data: Record<string, any> = {}
    ): Promise<{ success: boolean; message: string }> {
        if (import.meta.env.DEV) {
            const userId = await this.getCurrentUserId();

            return this.request<{ success: boolean; message: string }>('/api/web-push/test', {
                method: 'POST',
                body: JSON.stringify({
                    userId,
                    title,
                    body,
                    data: {
                        ...data,
                        isDebug: true
                    }
                })
            });
        }

        throw new Error('Esta función solo está disponible en entorno de desarrollo');
    }

    /**
     * Registra un token de dispositivo en el backend
     */
    async registerToken(token: string): Promise<{ success: boolean; data: DeviceToken }> {
        // Guardar el token para uso posterior
        this.setCurrentToken(token);

        const platform = Capacitor.getPlatform();
        const deviceType = this.deviceInfo?.platform === 'web' ? 'desktop' :
            (this.deviceInfo?.isTablet ? 'tablet' : 'mobile');

        // Usar el nuevo endpoint de registro de tokens
        return this.request<{ success: boolean; data: DeviceToken }>('/api/device-tokens/register', {
            method: 'POST',
            body: JSON.stringify({
                token,
                userId: await this.getCurrentUserId(),
                device: {
                    type: deviceType,
                    platform,
                    model: this.deviceInfo?.model,
                    appVersion: '1.0.0' // Actualizar según versión de la app
                }
            })
        });
    }

    /**
     * Actualiza las preferencias de notificaciones
     * Nota: Esta funcionalidad puede ser implementada en el modelo DeviceToken si se necesita
     */
    async updatePreferences(
        token: string,
        preferences: Partial<NotificationPreferences>
    ): Promise<{ preferences: NotificationPreferences }> {
        // Para mantener compatibilidad con el código existente
        // Esta funcionalidad tendría que ser implementada en el backend
        return this.request<{ preferences: NotificationPreferences }>('/api/notifications/preferences', {
            method: 'POST',
            body: JSON.stringify({
                token,
                preferences
            })
        });
    }

    /**
     * Elimina un token al cerrar sesión
     */
    async unregisterToken(token: string): Promise<{ success: boolean; message: string }> {
        // Usar el nuevo endpoint para desactivar tokens
        return this.request<{ success: boolean; message: string }>(`/api/device-tokens/deactivate/${token}`, {
            method: 'PUT'
        });
    }

    /**
     * Obtiene las preferencias de notificaciones
     * Nota: Esta funcionalidad puede ser implementada en el modelo DeviceToken si se necesita
     */
    async getPreferences(token: string): Promise<{ preferences: NotificationPreferences }> {
        // Para mantener compatibilidad con el código existente
        // Esta funcionalidad tendría que ser implementada en el backend
        return this.request<{ preferences: NotificationPreferences }>(
            `/api/notifications/preferences?token=${encodeURIComponent(token)}`
        );
    }

    /**
     * Tracks a notification open event with additional details
     */
    async trackNotificationOpen(
        notificationId: string,
        trackingInfo: {
            action?: 'view' | 'click' | 'dismiss';
            screen?: string;
            timeSpent?: number;
            actionTaken?: string;
            section?: string;
        } = {}
    ): Promise<{ success: boolean; data?: any }> {
        return this.request<{ success: boolean; data?: any }>(`/api/notifications/track/${notificationId}`, {
            method: 'POST',
            body: JSON.stringify({
                userId: await this.getCurrentUserId(),
                deviceId: this.currentToken,
                ...trackingInfo
            })
        });
    }

    /**
     * Marca una notificación como leída
     */
    async markAsRead(
        notificationId: string,
        screen: string = 'notification-list'
    ): Promise<{ success: boolean }> {
        return this.trackNotificationOpen(notificationId, {
            action: 'view',
            screen
        });
    }

    async trackNotificationClick(
        notificationId: string,
        screen: string = 'notification-list',
        actionTaken: string = 'default'
    ): Promise<{ success: boolean }> {
        return this.trackNotificationOpen(notificationId, {
            action: 'click',
            screen,
            actionTaken
        });
    }

    async trackNotificationDismiss(
        notificationId: string,
        screen: string = 'notification-list'
    ): Promise<{ success: boolean }> {
        return this.trackNotificationOpen(notificationId, {
            action: 'dismiss',
            screen
        });
    }

    async getNotificationAnalytics(notificationId: string): Promise<any> {
        return this.request<any>(`/api/notifications/${notificationId}/analytics`);
    }

    /**
     * Obtiene el historial de notificaciones
     */
    async getHistory(
        page: number = 1,
        limit: number = 20
    ): Promise<NotificationsHistoryResponse> {
        const userId = await this.getCurrentUserId();
        // Usar el nuevo endpoint para obtener notificaciones de usuario
        return this.request<NotificationsHistoryResponse>(
            `/api/notifications/user/${userId}?page=${page}&limit=${limit}`
        );
    }

    /**
     * Envía una notificación relacionada con un objetivo
     */
    async sendGoalNotification(
        goalId: string,
        title: string,
        body: string,
        notificationType: 'reminder' | 'milestone' | 'completion' | string
    ): Promise<{
        success: boolean;
        message: string;
        data: {
            successCount: number;
            failureCount: number;
            _id: string;
        }
    }> {
        const userId = await this.getCurrentUserId();

        // Usar el nuevo endpoint para enviar notificaciones a un usuario
        return this.request(
            '/api/notifications/send/user',
            {
                method: 'POST',
                body: JSON.stringify({
                    userId,
                    title,
                    body,
                    data: {
                        type: notificationType,
                        goalId,
                        route: '/app/goals/'
                    }
                })
            }
        );
    }

    /**
     * Envía una notificación a un usuario específico
     */
    async sendToUser(
        userId: string,
        title: string,
        body: string,
        data: Record<string, any> = {}
    ): Promise<{
        success: boolean;
        message: string;
        data: any;
    }> {
        return this.request(
            '/api/notifications/send/user',
            {
                method: 'POST',
                body: JSON.stringify({
                    userId,
                    title,
                    body,
                    data
                })
            }
        );
    }

    /**
     * Envía una notificación a múltiples usuarios
     */
    async sendToUsers(
        userIds: string[],
        title: string,
        body: string,
        data: Record<string, any> = {}
    ): Promise<{
        success: boolean;
        message: string;
        data: any;
    }> {
        return this.request(
            '/api/notifications/send/users',
            {
                method: 'POST',
                body: JSON.stringify({
                    userIds,
                    title,
                    body,
                    data
                })
            }
        );
    }

    /**
     * Programa una notificación para envío futuro
     */
    async scheduleNotification(
        title: string,
        body: string,
        sendAt: Date,
        userId?: string,
        userIds?: string[],
        data: Record<string, any> = {}
    ): Promise<{
        success: boolean;
        message: string;
        data: any;
    }> {
        // Definir los destinatarios
        const recipients: any = {};
        if (userId) {
            recipients.userId = userId;
        } else if (userIds && userIds.length) {
            recipients.userIds = userIds;
        } else {
            // Si no se especifica, enviar al usuario actual
            recipients.userId = await this.getCurrentUserId();
        }

        return this.request(
            '/api/notifications/schedule',
            {
                method: 'POST',
                body: JSON.stringify({
                    title,
                    body,
                    data,
                    recipients,
                    sendAt: sendAt.toISOString()
                })
            }
        );
    }

    /**
     * Función para pruebas: envía una notificación de prueba
     * Solo disponible en desarrollo
     */
    async sendTestNotification(
        userId: string,
        title: string,
        body: string,
        data?: any,
        type: string = 'activity'
    ): Promise<any> {
        if (import.meta.env.DEV) {
            // Usar el endpoint de notificaciones normal pero con un flag de debug
            return this.request(
                '/api/notifications/send/user',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        userId,
                        title,
                        body,
                        data: {
                            ...data,
                            type,
                            isDebug: true
                        }
                    })
                }
            );
        }

        throw new Error('Esta función solo está disponible en entorno de desarrollo');
    }



    // Método auxiliar para obtener el ID del usuario actual
    public async getCurrentUserId(): Promise<string> {
        try {
            const user = await firebaseAuth.getCurrentUser();

            if (!user || !user.uid) {
                throw new Error('Usuario no autenticado');
            }
            return user.uid;
        } catch (error) {
            console.error('Error al obtener el ID del usuario:', error);
            throw error;
        }
    }
}

export const notificationsAPI = new NotificationsAPI();